import axios, { InternalAxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { refreshToken as refreshTokenService } from '../services/auth.service';
import { buildAuthHeaders } from '../auth/headers';
import { demoActive } from '../demo/core';

export const axiosObject = axios.create();

axiosObject.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Demo tabs never talk to the network: any request that bypassed makeRequest is refused here.
    if (demoActive()) {
      throw Object.assign(new Error('[demo] blocked network call ' + config.method + ' ' + config.url), { isDemoBlocked: true });
    }
    const language = sessionStorage.getItem('language') || 'ar';
    let accessToken = sessionStorage.getItem('access_token');
    const refreshToken = sessionStorage.getItem('refresh_token');

    if (
      !config.url?.includes('/app_user/refresh_token') &&
      accessToken &&
      refreshToken
    ) {
      try {
        const decodedToken = jwtDecode(accessToken);
        if (decodedToken.exp) {
          const expirationTime = decodedToken.exp * 1000;

          if (Date.now() >= expirationTime) {
            const response = await refreshTokenService({ refresh_token: refreshToken });
            if (response?.result?.access_token) {
              accessToken = response.result.access_token;
              sessionStorage.setItem('access_token', response.result.access_token);
              sessionStorage.setItem('refresh_token', response.result.refresh_token);
              void import('../stores/auth.store').then(({ syncAuthStoreFromToken }) => {
                syncAuthStoreFromToken(response.result.access_token);
              });
            } else if (response?.access_token) {
              accessToken = response.access_token;
              sessionStorage.setItem('access_token', response.access_token);
              sessionStorage.setItem('refresh_token', response.refresh_token);
              void import('../stores/auth.store').then(({ syncAuthStoreFromToken }) => {
                syncAuthStoreFromToken(response.access_token);
              });
            } else {
              sessionStorage.removeItem('access_token');
              sessionStorage.removeItem('refresh_token');
              console.error('Failed to refresh token');
            }
          }
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
      }
    }

    const authHeaders = buildAuthHeaders(accessToken);

    const updatedConfig: InternalAxiosRequestConfig = {
      ...config,
      params: {
        ...config.params,
        lang: language.toUpperCase(),
      },
      headers: {
        ...config.headers,
        ...authHeaders,
      } as AxiosRequestHeaders,
    };

    return updatedConfig;
  },
  (error) => {
    console.error('error: interceptors', error);
    return Promise.reject(error);
  },
);

axiosObject.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401 && !demoActive()) {
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }

    if (status === 403) {
      const message =
        error.response?.data?.response?.message ??
        error.response?.data?.detail ??
        'You do not have permission to perform this action';
      console.warn('[403]', message);
    }

    return Promise.reject(error);
  },
);

export default axiosObject;
